import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../../context/UserContext';
import { supabase } from '../../../lib/supabase';
import { formatDate, formatDuration } from '../../../utils/dateUtils';
import { useTheme } from '../../../context/ThemeContext';
import { Client } from '../../../context/ClientContext';
import TimeEntryHistory from './functions/TimeEntryHistory';
import TimeEntryForm from './functions/TimeEntryForm';
import ReactApexChartModule from 'react-apexcharts';

// react-apexcharts is CJS; under Vite's ESM interop the default import can
// arrive as the module namespace object rather than the component, which
// makes <ReactApexChart> throw "Element type is invalid". Unwrap defensively.
const ReactApexChart = ((ReactApexChartModule as unknown as { default?: typeof ReactApexChartModule })
  .default ?? ReactApexChartModule) as typeof ReactApexChartModule;
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStop } from '@fortawesome/free-solid-svg-icons';
import { Avatar, Badge, Button, Card, Input, PulseDot } from '../../ui';

interface TimeClockSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

interface TimeEntryRecord {
  id: string;
  client_id: string;
  user_id: string;
  task_name: string;
  start_time: string;
  end_time: string | null;
  notes?: string | null;
}

type ActiveTimeEntry = TimeEntryRecord & {
  user?: {
    id: string;
    name: string | null;
    email: string | null;
  } | null;
};

export default function TimeClockSection({ client, expandedSection }: TimeClockSectionProps) {
  // Theme is only read for the ApexCharts config (third-party canvas, not tokenized)
  const { theme } = useTheme();
  const { user } = useUser();
  const [activeEntry, setActiveEntry] = useState<TimeEntryRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClockingIn, setIsClockingIn] = useState(false);
  const [isClockingOut, setIsClockingOut] = useState(false);
  const [activeEntries, setActiveEntries] = useState<ActiveTimeEntry[]>([]);
  const [activeElapsedTimes, setActiveElapsedTimes] = useState<Record<string, number>>({});
  const [isLoadingActiveEntries, setIsLoadingActiveEntries] = useState(true);

  // Reporting states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to last 30 days
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [hourlyRate, setHourlyRate] = useState(50); // Default $50/hour
  const [reportData, setReportData] = useState<TimeEntryRecord[]>([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const fetchActiveEntry = useCallback(async () => {
    if (!client?.id || !user?.id) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase.rpc('get_active_time_entry', {
        p_user_id: user.id,
        p_client_id: client.id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setActiveEntry(data[0]);
      } else {
        setActiveEntry(null);
      }
    } catch (err) {
      console.error('Error fetching active time entry:', err);
      setError('Failed to load time tracking data');
    } finally {
      setIsLoading(false);
    }
  }, [client?.id, user?.id]);

  // Fetch report data for the selected date range
  const fetchReportData = useCallback(async () => {
    if (!client?.id || !user?.id) return;

    setIsLoadingReport(true);
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('client_id', client.id)
        .gte('start_time', `${startDate}T00:00:00.000Z`)
        .lte('start_time', `${endDate}T23:59:59.999Z`)
        .not('end_time', 'is', null) // Only completed entries
        .order('start_time', { ascending: true });

      if (error) throw error;
      setReportData((data ?? []) as TimeEntryRecord[]);
    } catch (err) {
      console.error('Error fetching report data:', err);
    } finally {
      setIsLoadingReport(false);
    }
  }, [client?.id, user?.id, startDate, endDate]);

  const fetchActiveClockIns = useCallback(async () => {
    if (!client?.id) {
      setActiveEntries([]);
      setActiveElapsedTimes({});
      setIsLoadingActiveEntries(false);
      return;
    }

    try {
      setIsLoadingActiveEntries(true);

      const { data, error } = await supabase
        .from('time_entries')
        .select('id, client_id, user_id, task_name, start_time, end_time, notes')
        .eq('client_id', client.id)
        .is('end_time', null)
        .order('start_time', { ascending: true });

      if (error) throw error;

      if (!data || data.length === 0) {
        setActiveEntries([]);
        setActiveElapsedTimes({});
        return;
      }

      const typedData = (data ?? []) as TimeEntryRecord[];

      const userIds = Array.from(
        new Set(typedData.map((entry) => entry.user_id).filter((id): id is string => Boolean(id)))
      );

      let usersLookup: Record<string, { id: string; name: string | null; email: string | null }> = {};

      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, email')
          .in('id', userIds);

        if (usersError) {
          console.warn('Could not fetch user data for active clock-ins:', usersError);
        } else if (usersData) {
          usersLookup = usersData.reduce((acc, current) => {
            acc[current.id] = current;
            return acc;
          }, {} as Record<string, { id: string; name: string | null; email: string | null }>);
        }
      }

      const entriesWithUsers: ActiveTimeEntry[] = typedData.map((entry) => ({
        ...entry,
        user: entry.user_id ? usersLookup[entry.user_id] || null : null,
      }));

      setActiveEntries(entriesWithUsers);
    } catch (err) {
      console.error('Error fetching active clock-ins:', err);
      setError('Failed to load active clock-ins');
      setActiveEntries([]);
      setActiveElapsedTimes({});
    } finally {
      setIsLoadingActiveEntries(false);
    }
  }, [client?.id]);

  useEffect(() => {
    if (expandedSection === 'timeclock') {
      fetchActiveEntry();
      fetchReportData();
      fetchActiveClockIns();
    }
  }, [expandedSection, fetchActiveClockIns, fetchActiveEntry, fetchReportData]);

  useEffect(() => {
    if (activeEntries.length === 0) {
      setActiveElapsedTimes({});
      return;
    }

    const updateElapsedTimes = () => {
      const now = Date.now();
      const updatedTimes = activeEntries.reduce((acc, entry) => {
        const start = new Date(entry.start_time).getTime();
        acc[entry.id] = Math.max(0, Math.floor((now - start) / 1000));
        return acc;
      }, {} as Record<string, number>);

      setActiveElapsedTimes(updatedTimes);
    };

    updateElapsedTimes();
    const intervalId = setInterval(updateElapsedTimes, 1000);

    return () => clearInterval(intervalId);
  }, [activeEntries]);

  // Update elapsed time every second when there's an active entry
  useEffect(() => {
    if (!activeEntry) {
      setElapsedTime(0);
      return;
    }

    const startTime = new Date(activeEntry.start_time).getTime();

    // Initial calculation
    setElapsedTime(Math.floor((Date.now() - startTime) / 1000));

    // Set up interval
    const intervalId = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    // Clean up
    return () => clearInterval(intervalId);
  }, [activeEntry]);

  // Clock in function
  const handleClockIn = async (startTime: Date, taskName: string, notes?: string) => {
    if (!client?.id || !user?.id) return;

    try {
      setIsClockingIn(true);
      setError(null);

      const { data, error } = await supabase.rpc('clock_in', {
        p_client_id: client.id,
        p_task_name: taskName,
        p_start_time: startTime.toISOString(),
        p_notes: notes || undefined,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setActiveEntry(data[0]);
      }

      fetchActiveClockIns();
    } catch (err) {
      console.error('Error clocking in:', err);
      setError('Failed to clock in');
    } finally {
      setIsClockingIn(false);
    }
  };

  // Clock out function
  const handleClockOut = async (endTime: Date = new Date()) => {
    if (!activeEntry?.id) return;

    try {
      setIsClockingOut(true);
      setError(null);

      const { error } = await supabase.rpc('clock_out', {
        p_time_entry_id: activeEntry.id,
        p_end_time: endTime.toISOString(),
      });

      if (error) throw error;

      setActiveEntry(null);
      fetchActiveClockIns();
      fetchReportData();
    } catch (err) {
      console.error('Error clocking out:', err);
      setError('Failed to clock out');
    } finally {
      setIsClockingOut(false);
    }
  };

  // Update start time for active entry
  const handleUpdateStartTime = async (newStartTime: Date) => {
    if (!activeEntry?.id) return;

    try {
      setError(null);

      const { error } = await supabase
        .from('time_entries')
        .update({
          start_time: newStartTime.toISOString(),
        })
        .eq('id', activeEntry.id);

      if (error) throw error;

      // Update the local state
      setActiveEntry({
        ...activeEntry,
        start_time: newStartTime.toISOString(),
      });

      fetchActiveClockIns();
    } catch (err) {
      console.error('Error updating start time:', err);
      setError('Failed to update start time');
    }
  };

  // Calculate total hours and earnings for the report period
  const calculateTotals = () => {
    let totalSeconds = 0;
    reportData.forEach((entry) => {
      if (entry.end_time) {
        const start = new Date(entry.start_time).getTime();
        const end = new Date(entry.end_time).getTime();
        totalSeconds += (end - start) / 1000;
      }
    });

    const totalHours = totalSeconds / 3600;
    const totalEarnings = totalHours * hourlyRate;

    return { totalHours, totalEarnings };
  };

  // Prepare chart data
  const getChartData = () => {
    const dailyData = new Map<string, number>();

    reportData.forEach((entry) => {
      if (entry.end_time) {
        const date = new Date(entry.start_time).toISOString().split('T')[0];
        const start = new Date(entry.start_time).getTime();
        const end = new Date(entry.end_time).getTime();
        const hours = (end - start) / (1000 * 3600);

        dailyData.set(date, (dailyData.get(date) || 0) + hours);
      }
    });

    const sortedDates = Array.from(dailyData.keys()).sort();
    const hours = sortedDates.map((date) => dailyData.get(date) || 0);

    return {
      dates: sortedDates,
      hours,
    };
  };

  const chartData = getChartData();
  const { totalHours, totalEarnings } = calculateTotals();

  const chartOptions = {
    chart: {
      type: 'area' as const,
      height: 200,
      toolbar: { show: false },
      background: 'transparent',
      fontFamily: 'Geist Mono, ui-monospace, monospace',
    },
    theme: {
      mode: theme as 'light' | 'dark',
    },
    colors: ['#d95636'],
    stroke: {
      curve: 'smooth' as const,
      width: 2,
    },
    fill: {
      type: 'gradient',
      gradient: {
        opacityFrom: 0.4,
        opacityTo: 0.1,
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: chartData.dates,
      labels: {
        style: {
          colors: '#8B8689',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: {
          colors: '#8B8689',
        },
        // ApexCharts printed small ranges as "1.5000000", which also ate a phone's narrow plot width
        formatter: (value: number) => `${Number(value.toFixed(1))}h`,
      },
    },
    grid: {
      borderColor: 'rgba(139, 134, 137, 0.18)',
    },
    tooltip: {
      theme: theme,
      y: {
        formatter: (value: number) => `${value.toFixed(1)} hours`,
      },
    },
  };

  if (expandedSection !== 'timeclock') return null;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
      {/* Time Clock Card */}
      <Card
        eyebrow="Time Clock"
        title={activeEntry ? 'Clocked In' : 'Not Tracking'}
        actions={
          activeEntry ? (
            <Button size="sm" onClick={() => handleClockOut()} disabled={isClockingOut}>
              <FontAwesomeIcon icon={faStop} style={{ fontSize: '11px', marginRight: '7px' }} />
              {isClockingOut ? 'Clocking Out...' : 'Clock Out'}
            </Button>
          ) : undefined
        }
      >
        {error && (
          <div
            style={{
              marginBottom: '14px',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
            <button
              onClick={() => setError(null)}
              className="hover-underline"
              style={{
                marginLeft: '8px',
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 'var(--text-xs)',
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <div
              className="animate-spin w-7 h-7 border-2 rounded-full"
              style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : (
          <>
            {activeEntry ? (
              <div
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--status-up-soft)',
                  border: '1px solid rgba(86, 162, 104, 0.25)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
                  <PulseDot tone="up" title="Timer running" />
                  <span className="t-eyebrow" style={{ color: 'var(--status-up)' }}>Running</span>
                </div>
                <div
                  className="t-num"
                  style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                    color: 'var(--text-body)',
                    letterSpacing: 'var(--tracking-tight)',
                    marginBottom: '10px',
                  }}
                >
                  {formatDuration(elapsedTime)}
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginBottom: '12px' }}>
                  {activeEntry.task_name}
                </div>

                <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Started At</div>
                <TimeEntryForm
                  initialTime={new Date(activeEntry.start_time)}
                  onSubmit={handleUpdateStartTime}
                  buttonLabel="Update"
                  isCompact={true}
                />

                {activeEntry.notes && (
                  <div style={{ marginTop: '12px' }}>
                    <div className="t-eyebrow" style={{ marginBottom: '5px' }}>Notes</div>
                    <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                      {activeEntry.notes}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <TimeEntryForm
                  onSubmit={(startTime, taskName, notes) => handleClockIn(startTime, taskName!, notes)}
                  isSubmitting={isClockingIn}
                />
              </div>
            )}

            {/* Who's clocked in */}
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
              >
                <span className="t-eyebrow">{"Who's Clocked In"}</span>
                {activeEntries.length > 0 && (
                  <Badge tone="up" dot pulse>{activeEntries.length} active</Badge>
                )}
              </div>

              {isLoadingActiveEntries ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                  <div
                    className="animate-spin w-5 h-5 border-2 rounded-full"
                    style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
                  />
                </div>
              ) : activeEntries.length === 0 ? (
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                  No team members are clocked in for this client right now.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {activeEntries.map((entry) => {
                    const durationSeconds = activeElapsedTimes[entry.id] ?? 0;
                    const displayName = entry.user?.name || entry.user?.email || 'Unknown User';
                    const isCurrentUserEntry = entry.user_id === user?.id;

                    return (
                      <div
                        key={entry.id}
                        className="hover-wash"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '9px 10px',
                          margin: '0 -10px',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        <Avatar name={displayName} size={24} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--text-body)',
                            }}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {entry.task_name}
                            </span>
                            <PulseDot tone="up" title="Timer running" />
                          </span>
                          <span
                            style={{
                              display: 'block',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--text-faint)',
                              marginTop: '2px',
                            }}
                          >
                            {displayName}
                            {isCurrentUserEntry ? ' · You' : ''}
                          </span>
                        </span>
                        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                          {formatDate(entry.start_time, 'MMM d, h:mm a')}
                        </span>
                        <span
                          className="t-num"
                          style={{ fontSize: 'var(--text-sm)', color: 'var(--status-up)', textAlign: 'right' }}
                        >
                          {formatDuration(durationSeconds)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Toggle for history */}
            <Button size="sm" variant="ghost" onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Hide History' : 'Show History'}
            </Button>

            {/* Time Entry History */}
            {showHistory && (
              <div style={{ marginTop: '14px' }}>
                <TimeEntryHistory clientId={client.id} />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Time Reports Card */}
      <Card
        eyebrow="Time Reports"
        title={totalHours > 0 ? `${totalHours.toFixed(1)}h logged` : 'No time logged'}
      >
        {/* Date Range and Rate Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
          {/* minmax(0, 1fr): a plain 1fr track won't shrink below a date input's intrinsic width, which pushed
              End Date past the right edge of a phone */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Start Date</div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="ck-date-input"
                style={{ minWidth: 0 }}
              />
            </div>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>End Date</div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="ck-date-input"
                style={{ minWidth: 0 }}
              />
            </div>
          </div>

          <Input
            label="Hourly Rate ($)"
            type="number"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(Number(e.target.value))}
            min="0"
            step="5"
          />
        </div>

        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '20px' }}>
          <div
            style={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 12px',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '4px' }}>Total Hours</div>
            <div
              className="t-num"
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
              }}
            >
              {totalHours.toFixed(1)}
            </div>
          </div>
          <div
            style={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 12px',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '4px' }}>Total Earnings</div>
            <div
              className="t-num"
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--status-up)',
              }}
            >
              ${totalEarnings.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Chart */}
        {isLoadingReport ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <div
              className="animate-spin w-6 h-6 border-2 rounded-full"
              style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : chartData.dates.length > 0 ? (
          <div style={{ marginBottom: '20px' }}>
            <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Daily Hours</div>
            <ReactApexChart
              options={chartOptions}
              series={[{ name: 'Hours', data: chartData.hours }]}
              type="area"
              height={150}
            />
          </div>
        ) : (
          <p style={{ margin: '0 0 20px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            No time entries in the selected range.
          </p>
        )}

        {/* Time Entries List */}
        {reportData.length > 0 && (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '8px',
              }}
            >
              <span className="t-eyebrow">Entries</span>
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {reportData.length}
              </span>
            </div>
            <div style={{ maxHeight: '256px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              {reportData.map((entry) => {
                const start = new Date(entry.start_time);
                const end = new Date(entry.end_time as string);
                const durationSeconds = (end.getTime() - start.getTime()) / 1000;
                const hours = durationSeconds / 3600;
                const earnings = hours * hourlyRate;

                return (
                  <div
                    key={entry.id}
                    className="hover-wash"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 10px',
                      margin: '0 -10px',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-body)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {entry.task_name}
                      </span>
                      <span
                        className="t-num"
                        style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}
                      >
                        {formatDate(start, 'MMM d, h:mm a')} – {formatDate(end, 'h:mm a')} ·{' '}
                        {formatDuration(Math.floor(durationSeconds))}
                      </span>
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      <span className="t-num" style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--status-up)' }}>
                        ${earnings.toFixed(2)}
                      </span>
                      <span className="t-num" style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                        {hours.toFixed(1)}h
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
